import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { ALERT_TYPES, ALERT_TYPE_LABELS, AlertType } from '../../config/alertTypes';
import { SERIES_COLORS, TEAM_AVERAGE_COLOR } from '../../config/dashboardTheme';
import RepComparePicker from './RepComparePicker';
import TrendChart from './TrendChart';
import { ScoreTrends, TrendLine } from './trendTypes';

interface ScoreTrendsSectionProps {
  trends: ScoreTrends | null;
  loading: boolean;
  /** True when viewing the whole organization, which is when reps can be compared. */
  isOrgWide: boolean;
  selectedRepIds: string[];
  onSelectedRepsChange: (repIds: string[]) => void;
}

const BUCKET_LABEL_FORMATS: Record<ScoreTrends['bucketUnit'], string> = {
  day: 'MMM d',
  week: 'MMM d',
  month: 'MMM yyyy',
};

const TEAM_LINE_KEY = 'teamAverage';
const PANEL_CHART_HEIGHT = 170;

/**
 * Playbook adoption runs a fraction of calls on real tenants, and a given item
 * type may appear on only a handful. Below this many scored sessions the line is
 * a couple of points in an otherwise empty chart, which reads as a trend when it
 * is not one — so it gets called out rather than drawn silently.
 */
const MIN_SESSIONS_FOR_TREND = 5;

function Card({ children }: { children: React.ReactNode }) {
  return <section className="rounded-lg border border-hairline bg-surface p-5">{children}</section>;
}

/**
 * One shared legend for the whole section. Colour means rep everywhere; the
 * organization is always the dashed ink line, never a hue.
 */
function SharedLegend({ repLines }: { repLines: TrendLine[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-ink-secondary">
      <span className="flex items-center gap-1.5">
        <svg width="22" height="2" aria-hidden>
          <line x1="0" y1="1" x2="22" y2="1" stroke={TEAM_AVERAGE_COLOR} strokeWidth="2.5" strokeDasharray="6 3" />
        </svg>
        Organization average
      </span>
      {repLines.map((line) => (
        <span key={line.key} className="flex items-center gap-1.5">
          <svg width="22" height="2" aria-hidden>
            <line x1="0" y1="1" x2="22" y2="1" stroke={line.color} strokeWidth="2" />
          </svg>
          {line.label}
        </span>
      ))}
    </div>
  );
}

const ScoreTrendsSection: React.FC<ScoreTrendsSectionProps> = ({
  trends,
  loading,
  isOrgWide,
  selectedRepIds,
  onSelectedRepsChange,
}) => {
  const bucketLabels = useMemo(() => {
    if (!trends?.buckets) return [];
    const pattern = BUCKET_LABEL_FORMATS[trends.bucketUnit] ?? 'MMM d';
    return trends.buckets.map((bucket) => format(new Date(bucket), pattern));
  }, [trends?.buckets, trends?.bucketUnit]);

  /** A rep keeps one colour across every panel and both rows. */
  const repColorById = useMemo(() => {
    const colors = new Map<string, string>();
    selectedRepIds.forEach((repId, index) => {
      colors.set(repId, SERIES_COLORS[index % SERIES_COLORS.length]);
    });
    return colors;
  }, [selectedRepIds]);

  const orgLabel = isOrgWide ? 'Organization average' : 'Average';

  const legendRepLines = useMemo<TrendLine[]>(
    () =>
      (trends?.alertTrends.reps ?? [])
        .filter((rep) => selectedRepIds.includes(rep?.userId))
        .map((rep) => ({
          key: rep.userId,
          label: rep.userEmail,
          color: repColorById.get(rep.userId) ?? SERIES_COLORS[0],
          values: [],
        })),
    [trends, selectedRepIds, repColorById]
  );

  if (loading && !trends) {
    return (
      <div className="mb-6 space-y-4">
        <div className="h-96 animate-pulse rounded-lg border border-hairline bg-surface-subtle" />
        <div className="h-72 animate-pulse rounded-lg border border-hairline bg-surface-subtle" />
      </div>
    );
  }

  if (!trends) return null;

  const playbookCount = trends.playbookTrends?.sessionCount ?? 0;

  // admin-portal and backend deploy independently, so a newer page can meet an
  // older API that does not send this count. Rather than render "0 of 0", drop
  // the denominator and show only what we actually know.
  const analyzed = typeof trends.analyzedSessionCount === 'number' ? trends.analyzedSessionCount : null;

  // A tenant with nothing analyzed yet — every new tenant on day one — gets one
  // short explanation rather than five empty charts each captioned "0 of 0".
  if (analyzed === 0) {
    return (
      <div className="mb-6">
        <Card>
          <h2 className="heading-4 font-semibold">Scoring trends</h2>
          <p className="mt-1 text-xs text-ink-muted">
            No analyzed sessions in this period. Scores appear here once calls have been
            recorded and post-call analysis has run.
          </p>
        </Card>
      </div>
    );
  }

  /** "3 of 5 sessions" — every panel has its own denominator. */
  const denominator = (count: number) =>
    analyzed === null
      ? `${count} session${count === 1 ? '' : 's'}`
      : `${count} of ${analyzed} session${analyzed === 1 ? '' : 's'}`;

  const linesForType = (type: AlertType): TrendLine[] => {
    const teamLine: TrendLine = {
      key: TEAM_LINE_KEY,
      label: orgLabel,
      color: TEAM_AVERAGE_COLOR,
      values: trends.alertTrends.team?.[type] ?? [],
      isTeamAverage: true,
    };
    const repLines = (trends.alertTrends.reps ?? [])
      .filter((rep) => selectedRepIds.includes(rep?.userId))
      .map((rep) => ({
        key: rep.userId,
        label: rep.userEmail,
        color: repColorById.get(rep.userId) ?? SERIES_COLORS[0],
        values: rep.series?.[type] ?? [],
      }));
    return [teamLine, ...repLines];
  };

  const playbookLines: TrendLine[] = [
    {
      key: TEAM_LINE_KEY,
      label: orgLabel,
      color: TEAM_AVERAGE_COLOR,
      values: trends.playbookTrends.team ?? [],
      isTeamAverage: true,
    },
    ...(trends.playbookTrends.reps ?? [])
      .filter((rep) => selectedRepIds.includes(rep?.userId))
      .map((rep) => ({
        key: rep.userId,
        label: rep.userEmail,
        color: repColorById.get(rep.userId) ?? SERIES_COLORS[0],
        values: rep.series ?? [],
      })),
  ];

  return (
    <div className="mb-6 space-y-4">
      {/* Row 1 — knowledge base adherence, one panel per item type */}
      <Card>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="heading-4 font-semibold">Knowledge Base Adherence</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              How well each knowledge base item type is handled over time.
            </p>
          </div>
          {isOrgWide && (
            <RepComparePicker
              reps={trends.availableReps ?? []}
              selectedRepIds={selectedRepIds}
              onChange={onSelectedRepsChange}
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
          {ALERT_TYPES.map((type) => {
            const rawCount = trends.alertTrends.sessionCountsByType?.[type];
            const count = typeof rawCount === 'number' ? rawCount : null;
            const isSparse = count !== null && count > 0 && count < MIN_SESSIONS_FOR_TREND;
            return (
              <div key={type} className="rounded-lg border border-hairline p-3">
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-ink">{ALERT_TYPE_LABELS[type]}</h3>
                  {count !== null && (
                    <span className="text-xs text-ink-muted">{denominator(count)}</span>
                  )}
                </div>
                <TrendChart
                  lines={linesForType(type)}
                  bucketLabels={bucketLabels}
                  emptyMessage={`No ${ALERT_TYPE_LABELS[type].toLowerCase()} scored in this period`}
                  height={PANEL_CHART_HEIGHT}
                  showLegend={false}
                />
                {isSparse && (
                  <p className="mt-1 text-xs text-warning">
                    Too few sessions to read as a trend.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 border-t border-hairline pt-3">
          <SharedLegend repLines={legendRepLines} />
        </div>
      </Card>

      {/* Row 2 — playbook adherence, full width */}
      <Card>
        <div className="mb-4">
          <h2 className="heading-4 font-semibold">Playbook Adherence</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            {denominator(playbookCount)} used a playbook. Only those are scored here.
          </p>
        </div>
        <TrendChart
          lines={playbookLines}
          bucketLabels={bucketLabels}
          emptyMessage="No sessions used a playbook in this period"
          showLegend={false}
        />
        {playbookCount > 0 && playbookCount < MIN_SESSIONS_FOR_TREND && (
          <p className="mt-2 text-xs text-warning">
            Too few playbook sessions to read as a trend. Widen the date range.
          </p>
        )}
        <div className="mt-4 border-t border-hairline pt-3">
          <SharedLegend repLines={legendRepLines} />
        </div>
      </Card>
    </div>
  );
};

export default ScoreTrendsSection;
